import { warnUnknownValueOnce } from '@/components/common/unknown-value'

import type {
	Investigation,
	InvestigationConclusion,
	InvestigationConclusionSummary,
	InvestigationSummary,
	InvestigationWire,
	Verdict,
} from '../types'

/**
 * The two verdicts the backend enum can produce — exhaustive over `Verdict`, so
 * a member added later is a compile error here (ADR 0020 §5).
 */
const VERDICTS: Record<Verdict, true> = { benign: true, malicious: true }

/**
 * Whether a value off the wire is actually a `Verdict`. The field is *typed*
 * `Verdict | null`, but it arrives as untrusted JSON — this check is what lets
 * everything downstream take the narrowed type at face value.
 */
const isVerdict = (value: unknown): value is Verdict =>
	typeof value === 'string' && value in VERDICTS

/**
 * The verdict/confidence pair, or `null` when the job reached no conclusion.
 *
 * This is the ONE gate. The backend's `InvestigationConclusion` requires both
 * fields, and the serializer writes them from a single `safe_parse_conclusion`
 * result, so "verdict validates and confidence is a real number" is exactly
 * "a conclusion exists". A payload carrying one without the other is
 * off-contract — pydantic would have rejected it too — so it degrades to the
 * absence rather than half a conclusion, and says so.
 *
 * @param wire - The flat investigation block, list or detail width.
 */
const toConclusionSummary = (
	wire: InvestigationWire,
): InvestigationConclusionSummary | null => {
	const { verdict, confidence } = wire

	if (!isVerdict(verdict)) {
		// A non-null value that is not a verdict is a contract surprise, so it is
		// warned about rather than silently absorbed (ADR 0020 §5's runtime half).
		// An absent verdict is a MODELLED state and must not warn — it is the
		// whole point of this branch.
		if (verdict) warnUnknownValueOnce('investigation verdict', String(verdict))
		return null
	}

	if (typeof confidence !== 'number' || !Number.isFinite(confidence)) {
		warnUnknownValueOnce('investigation confidence', String(confidence))
		return null
	}

	return { verdict, confidence }
}

/**
 * Fold the case-LIST investigation block into its view model.
 *
 * The list payload has no full-conclusion fields, so this deliberately builds
 * only the summary pair — it must never invent empty `reasoning`/`triage_steps`
 * for a row that simply wasn't asked for them.
 *
 * @param wire - The `investigation` block on a case-list row; `null` when the
 *   case has no investigation job at all (a different fact from "no verdict").
 */
export const toInvestigationSummary = (
	wire: InvestigationWire | null | undefined,
): InvestigationSummary | null => {
	if (!wire) return null

	return {
		status: wire.status,
		completed_at: wire.completed_at ?? null,
		conclusion: toConclusionSummary(wire),
	}
}

/**
 * Fold the case-DETAIL investigation block into its view model.
 *
 * The six conclusion fields hang off one `if conclusion else None` in the
 * serializer, so they are folded into one nullable object here and the coalesces
 * below only ever run for a conclusion the backend built (they cover the older
 * rows whose stored model predates a field). Everything downstream gets the
 * invariant from the compiler instead of re-deriving it from six null checks.
 *
 * @param wire - The `investigation` block at the case-detail response root;
 *   `null`/absent when the case has no investigation job.
 */
export const toInvestigation = (
	wire: InvestigationWire | null | undefined,
): Investigation | null => {
	if (!wire) return null

	const summary = toConclusionSummary(wire)
	const conclusion: InvestigationConclusion | null = summary && {
		...summary,
		behavior_insights: wire.behavior_insights ?? null,
		reasoning: wire.reasoning ?? [],
		triage_steps: wire.triage_steps ?? [],
		narrative_report: wire.narrative_report ?? '',
	}

	return {
		status: wire.status,
		created_at: wire.created_at ?? null,
		started_at: wire.started_at ?? null,
		completed_at: wire.completed_at ?? null,
		conclusion,
	}
}
